import React from 'react'
import { Link } from 'react-router-dom'
import {Sparkles, Film, ChevronRight, Home} from 'lucide-react'

export function CategoryClassics() {
  const classicShows = [
    {
      id: 1,
      title: 'The Smurfs Magic Flute',
      description: 'Relive the adventure of The Smurfs and the Magic Flute. A timeless tale that defined a generation of animation lovers.',
      image: 'https://static.lumi.new/87/8717cfaa1cc47f76a788c90416e49295.png',
      link: '/review/smurfs-magic-flute',
      year: '1976',
      keywords: 'smurfs magic flute, the smurfs and the magic flute, vintage animation'
    },
    {
      id: 2,
      title: 'Magical Maestro',
      description: 'A golden age animation masterpiece featuring theatrical magic and musical mayhem from the 1950s.',
      image: 'https://static.lumi.new/5c/5c4ca435178b44ad325564f132b0826e.png',
      year: '1952',
      keywords: 'magical maestro, vintage cartoon, golden age animation'
    },
    {
      id: 3,
      title: 'Sinbad and His Magic Belt',
      description: 'Embark on an Arabian Nights adventure filled with mystical artifacts and daring quests from retro animation.',
      image: 'https://static.lumi.new/38/38c45e0c24502fee403f9873841cacc6.png',
      year: '1975',
      keywords: 'sinbad and his magic belt, arabian nights, retro cartoon'
    },
    {
      id: 4,
      title: 'Fantasia',
      description: 'Disney\'s groundbreaking masterpiece combining classical music with stunning magical animation sequences.',
      image: 'https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '1940',
      keywords: 'disney magic, classical animation, sorcerer\'s apprentice'
    },
    {
      id: 5,
      title: 'The Wizard of Oz (Animated)',
      description: 'Classic animated adaptations of the beloved tale of Dorothy\'s journey through the magical land of Oz.',
      image: 'https://images.pexels.com/photos/6985003/pexels-photo-6985003.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '1960s',
      keywords: 'wizard of oz, classic fantasy, emerald city'
    },
    {
      id: 6,
      title: 'He-Man and the Masters of the Universe',
      description: 'By the power of Grayskull! The classic 80s series featuring magical swords, castles, and epic battles.',
      image: 'https://images.pexels.com/photos/6985092/pexels-photo-6985092.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '1983',
      keywords: 'he-man, 80s cartoons, magical sword'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-pink-500" />
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-500 via-orange-500 to-teal-500 bg-clip-text text-transparent">
                MagicCartoon.com
              </h1>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-600 hover:text-pink-500 font-semibold transition-colors"
            >
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-4">
            <Film className="w-16 h-16 text-white animate-pulse" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Classic Magic Cartoons
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Journey back to the golden age of animation. Timeless tales of magic and wonder from the vintage era.
          </p>
        </div>
      </section>

      {/* Shows Grid */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {classicShows.map((show) => (
              <div 
                key={show.id}
                className="bg-white rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-all duration-300 border-2 border-amber-200"
              >
                <div className="relative">
                  <img 
                    src={show.image}
                    alt={show.title}
                    className="w-full h-64 object-cover"
                  />
                  <span className="absolute top-4 right-4 bg-amber-500 text-white px-4 py-2 rounded-full font-bold text-sm">
                    {show.year}
                  </span>
                </div>
                <div className="p-6 space-y-4">
                  <h3 className="text-2xl font-bold text-gray-800">{show.title}</h3>
                  <p className="text-gray-600">{show.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {show.keywords.split(', ').map((keyword, idx) => (
                      <span 
                        key={idx}
                        className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                  {show.link ? (
                    <Link 
                      to={show.link}
                      className="group bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 w-fit"
                    >
                      Read More
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ) : (
                    <button className="group bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2">
                      Coming Soon
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
